import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { OpenInNew } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, Grid, Stack, Tab, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Link } from 'react-router-dom';
import { useFetch } from '../../../hooks/useFecth';
import { formatDate } from '../../Club/components/DialogSubscriber';

const roleLabels = {
    owner: 'Proprietário',
    manager: 'Gerente',
    admin: 'Administrador',
    user: 'Atendente',
};

function ProfileAccountSection({ user, roleLabel }) {
    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Dados do usuário
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    O e-mail de acesso não muda por aqui. Para trocar a senha, use a recuperação por e-mail. O admin Eurobem também pode redefinir.
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Usuário (login)" value={user?.userName || ''} disabled />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ height: '100%' }}>
                            <Typography variant="body2" color="text.secondary">
                                Permissão:
                            </Typography>
                            <Chip size="small" label={roleLabel} color="primary" variant="outlined" />
                        </Stack>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

function ProfileContractSection({ profile, isLoading }) {
    const signedPdfUrl =
        profile?.contract?.Uploads?.find(i => i.description === 'contrato_assinado')?.fileUrl ||
        profile?.contract?.Uploads?.find(i => i.description === 'contrato_original')?.fileUrl;
    const hasContractSigned =
        Boolean(profile?.contract?.signedAt) || ['CONCLUDED', 'SIGNED'].includes(profile?.contract?.status);

    if (isLoading) {
        return (
            <Typography variant="body2" color="text.secondary">
                Carregando informações do contrato…
            </Typography>
        );
    }

    return (
        <Card>
            <CardContent>
                <Stack spacing={2}>
                    <Typography variant="h6" gutterBottom>
                        Contrato
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Acompanhe o status e acesse o PDF do contrato quando estiver disponível nos uploads.
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <Typography variant="body2" color="text.secondary">
                            Status:
                        </Typography>
                        <Chip
                            size="small"
                            color={hasContractSigned ? 'success' : 'default'}
                            label={hasContractSigned ? 'Assinado' : profile?.contract?.status || 'Pendente'}
                            variant="outlined"
                        />
                        {profile?.contract?.signedAt && (
                            <> <Typography variant="body2" color="text.secondary">
                                Assinado em:
                            </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {profile?.contract?.signedAt ? formatDate(profile?.contract?.signedAt) : ''}
                                </Typography>
                            </>
                        )}

                    </Stack>

                    {hasContractSigned && signedPdfUrl ? (
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<OpenInNew />}
                            component="a"
                            href={signedPdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Abrir contrato assinado
                        </Button>
                    ) : null}

                    {hasContractSigned && !signedPdfUrl ? (
                        <Typography variant="body2" color="text.secondary">
                            O contrato consta como assinado, mas o arquivo ainda não está disponível para download. Tente novamente em instantes.
                        </Typography>
                    ) : null}

                    {!hasContractSigned ? (
                        <Stack spacing={1.5}>
                            <Typography variant="body2" color="text.secondary">
                                Para gerar ou assinar o contrato, acesse a página dedicada ao fluxo com o ZapSign.
                            </Typography>
                            <Button component={Link} to="/anunciante/contrato" variant="contained">
                                Ir para assinatura do contrato
                            </Button>
                        </Stack>
                    ) : null}
                </Stack>
            </CardContent>
        </Card>
    );
}

/** Conta do usuário logado (login e senha). Dados do anunciante ficam em `AdvertiserData`. */
export default function Profile() {
    const [{ user }] = useCookies(['user']);
    const [tab, setTab] = useState('account');
    const { data: profile, isLoading: isProfileLoading } = useFetch('/advertiser/profile');
    const roleLabel = useMemo(() => roleLabels[user?.role] || user?.role || '—', [user?.role]);

    return (
        <Stack spacing={3}>
            <Typography variant="h4" fontWeight="bold">
                Minha conta
            </Typography>

            <TabContext value={tab}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList onChange={(e, newTab) => setTab(newTab)}>
                        <Tab label="Conta" value="account" />
                        <Tab label="Contrato" value="contract" />
                    </TabList>
                </Box>
                <TabPanel value="account" sx={{ px: 0 }}>
                    <ProfileAccountSection user={user} roleLabel={roleLabel} />
                </TabPanel>
                <TabPanel value="contract" sx={{ px: 0 }}>
                    <ProfileContractSection profile={profile} isLoading={isProfileLoading} />
                </TabPanel>
            </TabContext>
        </Stack>
    );
}
